// LogDetailDialog.js
// Extracted from AIWorkforcePage.js for better maintainability

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../../components/ui/avatar';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../components/ui/collapsible';
import { Bot, Brain, Cpu, Sparkles, ChevronDown, Copy } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

/**
 * Dialog for viewing detailed AI execution logs with multi-brain analysis
 * 
 * @param {Object} props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {function} props.onOpenChange - Callback when dialog open state changes
 * @param {Object} props.log - The selected log to display
 * @param {function} props.onClose - Callback when close is clicked
 */
export const LogDetailDialog = ({
  open,
  onOpenChange,
  log,
  onClose
}) => {
  const handleCopyLog = () => {
    navigator.clipboard.writeText(JSON.stringify(log, null, 2));
    toast.success('Log copied to clipboard');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Avatar className="h-10 w-10 bg-gradient-to-br from-violet-500 to-purple-600">
              <AvatarFallback className="bg-transparent text-white font-bold">
                <Bot className="h-5 w-5" />
              </AvatarFallback>
            </Avatar>
            <div>
              <span>AI Execution Analysis</span>
              <div className="flex gap-2 mt-1">
                <Badge className={log?.success ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                  {log?.success ? 'Success' : 'Failed'}
                </Badge>
                <Badge variant="outline">{log?.duration_ms}ms</Badge>
              </div>
            </div>
          </DialogTitle>
          <DialogDescription>
            {log?.agent_name} • {log?.action} • {log?.timestamp ? format(new Date(log.timestamp), 'MMM d, yyyy HH:mm') : ''}
          </DialogDescription>
        </DialogHeader>
        
        {log && (
          <div className="space-y-4 py-4">
            {/* Task Info */}
            {log.task_title && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <Label className="text-sm font-medium">Task</Label>
                <p className="text-sm mt-1">{log.task_title}</p>
              </div>
            )}

            {/* Multi-Brain Analysis Results */}
            <div className="space-y-3">
              <h4 className="font-medium flex items-center gap-2">
                <Brain className="h-4 w-4" />
                Multi-Brain Analysis
              </h4>
              
              {/* Left Brain */}
              <Collapsible defaultOpen>
                <CollapsibleTrigger className="w-full p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2 hover:bg-blue-100 transition-colors">
                  <Cpu className="h-4 w-4 text-blue-600" />
                  <span className="font-medium text-blue-700">Left Brain (Analytical)</span>
                  <Badge variant="outline" className="text-xs bg-blue-100 text-blue-600">GPT-4o</Badge>
                  <ChevronDown className="h-4 w-4 text-blue-600 ml-auto" />
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-2">
                  <ScrollArea className="max-h-[300px] bg-blue-50/50 border border-blue-100 rounded-lg p-4">
                    <div className="prose prose-sm max-w-none">
                      <ReactMarkdown>{log.left_brain_output || 'No output available'}</ReactMarkdown>
                    </div>
                  </ScrollArea>
                </CollapsibleContent>
              </Collapsible>

              {/* Right Brain */}
              <Collapsible defaultOpen>
                <CollapsibleTrigger className="w-full p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-center gap-2 hover:bg-purple-100 transition-colors">
                  <Sparkles className="h-4 w-4 text-purple-600" />
                  <span className="font-medium text-purple-700">Right Brain (Creative)</span>
                  <Badge variant="outline" className="text-xs bg-purple-100 text-purple-600">Claude Sonnet 4.5</Badge>
                  <ChevronDown className="h-4 w-4 text-purple-600 ml-auto" />
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-2">
                  <ScrollArea className="max-h-[300px] bg-purple-50/50 border border-purple-100 rounded-lg p-4">
                    <div className="prose prose-sm max-w-none">
                      <ReactMarkdown>{log.right_brain_output || 'No output available'}</ReactMarkdown>
                    </div>
                  </ScrollArea>
                </CollapsibleContent>
              </Collapsible>

              {/* Cortex */}
              <Collapsible defaultOpen>
                <CollapsibleTrigger className="w-full p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2 hover:bg-emerald-100 transition-colors">
                  <Brain className="h-4 w-4 text-emerald-600" />
                  <span className="font-medium text-emerald-700">Cortex (Synthesis)</span>
                  <Badge className="text-xs bg-emerald-500 text-white">Gemini 3 Pro</Badge>
                  <Badge className="ml-2 bg-emerald-100 text-emerald-700">Final Output</Badge>
                  <ChevronDown className="h-4 w-4 text-emerald-600 ml-auto" />
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-2">
                  <ScrollArea className="max-h-[400px] bg-emerald-50/50 border border-emerald-100 rounded-lg p-4">
                    <div className="prose prose-sm max-w-none">
                      <ReactMarkdown>{log.brain_stem_output || 'No output available'}</ReactMarkdown>
                    </div>
                  </ScrollArea>
                </CollapsibleContent>
              </Collapsible>
            </div>

            {/* Metadata */}
            {log.metadata && (
              <div className="text-xs text-muted-foreground bg-slate-50 p-3 rounded-lg">
                <p className="font-medium mb-1">Metadata</p>
                <pre className="whitespace-pre-wrap">{JSON.stringify(log.metadata, null, 2)}</pre>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="log-detail-close">Close</Button>
          <Button onClick={handleCopyLog} data-testid="log-detail-copy">
            <Copy className="h-4 w-4 mr-2" />
            Copy Full Log
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LogDetailDialog;
